import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import serialize from 'serialize-javascript';

import { BodyCopy } from '@xingternal/typography';

import { DEFAULT_XING_HOME_PAGE, serpsRoutes } from '@/config';

import { ANY, TRANSLATIONS_MAP } from '../../constants';
import { useSerpsMatch } from '../../hooks';
import { capitalizeFirstLetter, getCategoryTranslation } from '../../utils';

import { ArrowLeft, Container, TextButtonLink } from './breadcrumb.styles';

export const Breadcrumb: React.VFC = () => {
  const serps = useSerpsMatch();
  const { formatMessage } = useIntl();

  if (!serps?.category || !serps.subcategory || !serps?.categoryValue || !serps?.subcategoryValue) return null;

  if (serps.category === ANY || serps.subcategory === ANY) return null;

  const { title, paramName, subparamName } = TRANSLATIONS_MAP[`${serps.category}-${serps.subcategory}`];

  const { category, subcategory, categoryValue, subcategoryValue } = serps;

  const categoryTranslation = capitalizeFirstLetter(getCategoryTranslation(formatMessage, category, categoryValue));
  const subCategoryTranslation = capitalizeFirstLetter(
    getCategoryTranslation(formatMessage, subcategory, subcategoryValue)
  );

  const breadcrumbs = [
    {
      url: DEFAULT_XING_HOME_PAGE,
      label: formatMessage({ id: 'XJM_BREADCRUMB_HOME' }),
    },
    {
      url:
        DEFAULT_XING_HOME_PAGE +
        generatePath(serpsRoutes[serps.subcategory], { categoryValue: serps.subcategoryValue }),
      label: subCategoryTranslation,
    },
    {
      url: DEFAULT_XING_HOME_PAGE + generatePath(serpsRoutes[serps.category], { categoryValue: serps.categoryValue }),
      label: categoryTranslation,
    },
    {
      label: formatMessage(
        {
          id: title,
        },
        {
          [paramName]: categoryTranslation,
          [subparamName]: subCategoryTranslation,
          ...(paramName === 'job_type' ? { employmentType: categoryTranslation } : {}),
          ...(paramName === 'term' ? { role: categoryTranslation } : {}),
        }
      ),
    },
  ];

  const jsonLD = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((breadcrumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: breadcrumb.label,
      ...(breadcrumb.url ? { item: breadcrumb.url } : {}),
    })),
  };

  return (
    <>
      <Container>
        {breadcrumbs.map((breadcrumb) => {
          const { url } = breadcrumb;

          const isLast = !url;

          return (
            <React.Fragment key={isLast ? 'last' : url}>
              {!isLast ? (
                <>
                  <TextButtonLink size="medium" to={url} fontWeight="regular">
                    {breadcrumb.label}
                  </TextButtonLink>
                  <ArrowLeft width={18} height={18} />
                </>
              ) : (
                <BodyCopy fontWeight="bold" noMargin>
                  {breadcrumb.label}
                </BodyCopy>
              )}
            </React.Fragment>
          );
        })}
      </Container>
      <Helmet script={[{ type: 'application/ld+json', innerHTML: serialize(jsonLD, { isJSON: true }) }]} />
    </>
  );
};
